import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isDemoUser } from "utils/demo";
import { DISPLAYED_ONBOARDING_PAYWALL } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import DiscoverBanner from "../Discover/Results/Banner";
import SpecialAnnouncementReferral from "../Discover/Results/Banner/SpecialAnnouncement/Referral";
import ActionItemList from "./ActionItemList";
import Agent from "./Agent";
import CustomDomainSection from "./CustomDomainSection";
import MetricsHome from "./Metrics";
import OnboardingStepper from "./OnboardingStepper";
import ResourcesHub from "./ResourcesHub";

const styles = {
  root: { pb: { xs: 3, md: 4 }, pt: { xs: 1, lg: 2 } },
  leftColumn: { pl: { xs: 4, md: 2, lg: 0 }, pr: { xs: 4, md: 2, lg: 3 } },
  rightColumn: { pr: { xs: 4, md: 2, lg: 0 }, pl: { xs: 4, md: 2, lg: 3 } },
  mainColumn: {
    pl: { xs: 4, md: 2, lg: 0 },
    pr: { xs: 4, md: 2, lg: 0 },
    pt: 2,
  },
};

const HomePage = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);
  const [fetchLoading, setFetchLoading] = useState(false);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const isDemoAccount = isDemoUser(currentOrg);

  const today = new Date();

  const getTodayDate = () => {
    return today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchActionItems = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items`,
      );
      setActionItems(res.actionItems);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchActionItems();
      const alreadyDisplayedOnboardingPaywall =
        localStorage.getItem(
          `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
        ) === "true";
      const hasSubscribed = subscription?.isSubscribed;
      if (
        !hasSubscribed &&
        !alreadyDisplayedOnboardingPaywall &&
        !currentOrg.isBrand
      ) {
        setUpgradeDialogSource("Onboarding Paywall");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentOrg?.isBrand) {
    return <Navigate to={`/${routes.inbox}`} replace />;
  }

  if (isDemoAccount) {
    return (
      <Grid container justifyContent="center">
        <Grid item xs={12} lg={discoverTab !== null ? 12 : 9}>
          <Routes>
            <Route
              path="/:agentId?"
              element={<Agent fetchLoading={fetchLoading} />}
            />
          </Routes>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center" sx={styles.root}>
      <Grid container rowSpacing={3} item xs={12} lg={11} xl={9}>
        <Grid item xs={12} sx={styles.mainColumn}>
          <DiscoverBanner />
        </Grid>
        <Grid
          item
          xs={12}
          md={discoverTab !== null ? 12 : 8}
          lg={discoverTab !== null ? 8 : 9}
          xl={discoverTab !== null ? 7 : 8}
          sx={styles.leftColumn}
        >
          <SpecialAnnouncementReferral />
          <Box display="flex">
            <Typography paragraph variant="h4">
              <Box
                component="i"
                className={
                  !profile?.hasOnboarded
                    ? "fa-solid fa-list"
                    : "fa-solid fa-rocket"
                }
                sx={{ pr: 1, color: "secondary.main" }}
              />{" "}
              Action Items for {currentOrg?.name}: {getTodayDate()}
            </Typography>
          </Box>
          {!profile?.hasOnboarded && (
            <Box display="flex">
              <Box sx={{ mt: 2 }}>
                <OnboardingStepper />
              </Box>
            </Box>
          )}
          {profile?.hasOnboarded && (
            <>
              <ActionItemList fetchLoading={fetchLoading} />
              <CustomDomainSection />
            </>
          )}
          {profile?.hasOnboarded && <MetricsHome actionItems={actionItems} />}
        </Grid>
        <Grid
          item
          xs={12}
          md={discoverTab !== null ? 12 : 4}
          lg={discoverTab !== null ? 4 : 3}
          xl={discoverTab !== null ? 5 : 4}
          sx={[
            styles.rightColumn,
            discoverTab !== null
              ? {
                  borderLeft: { lg: `1px solid ${grey[300]}` },
                }
              : {
                  borderLeft: { md: `1px solid ${grey[300]}` },
                },
          ]}
        >
          <ResourcesHub />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HomePage;
